<template>

    <div class="dropdown profileDropdown">
        <button ref="userButtonRef" class="userButton profileTrigger" data-bs-toggle="dropdown" data-bs-auto-close="outside" data-bs-display="static" aria-expanded="false" aria-label="Perfil y organización">
            <!-- Organization mark (rounded square) -->
            <img v-if="currentOrganizationImage" :src="currentOrganizationImage" class="orgAvatar" alt="Organización">
            <img v-else src="../../images/OrganizationIcon.png" class="orgAvatar" alt="Organización">

            <!-- User's own profile photo (circle) -->
            <img v-if="store.LoggedUser.ProfilePictureURL" :src="store.LoggedUser.ProfilePictureURL" class="userAvatar" alt="Perfil">
            <span v-else class="userAvatar userAvatar--initials">{{ store.LoggedUser.Initials }}</span>
        </button>

        <div ref="profileMenu" class="dropdown-menu profileMenu staticDropdownPanel">

            <!-- Identity: photo (editable), name, email, settings -->
            <div class="pm-identity">
                <button class="pm-avatar" @click="LoadProfileModal" aria-label="Editar foto de perfil">
                    <img v-if="store.LoggedUser.ProfilePictureURL != null" :src="store.LoggedUser.ProfilePictureURL"
                        class="pm-avatar__img" alt="Foto de perfil">
                    <span v-else class="pm-avatar__initials">{{ store.LoggedUser.Initials }}</span>

                    <span class="pm-avatar__overlay">
                        <i class="bi bi-pencil-fill"></i>
                    </span>
                </button>

                <div class="pm-identity__text">
                    <div class="pm-name">{{ store.LoggedUser.Name }}</div>
                    <div class="pm-email" :title="store.LoggedUser.Email">{{ store.LoggedUser.Email }}</div>
                </div>

                <button class="pm-iconBtn pm-identity__settings" @click="GoToConfig"
                    aria-label="Configuración" title="Configuración">
                    <i class="bi bi-gear-wide-connected"></i>
                </button>
            </div>

            <!-- Current organization: a settled fact -->
            <div class="pm-section">
                <div class="pm-section__label">Organización actual</div>

                <div class="pm-org pm-org--current">
                    <span class="pm-org__avatar">
                        <img v-if="currentOrganizationImage" :src="currentOrganizationImage" alt="">
                        <img v-else src="../../images/OrganizationIcon.png" alt="">
                    </span>

                    <span class="pm-org__name">{{ currentOrganization.Organization }}</span>

                    <button v-if="store.Actions.EDIT_MY_ORGANIZATION"
                        class="pm-iconBtn pm-org__edit"
                        @click="UpdateOrganization"
                        aria-label="Editar organización" title="Editar organización">
                        <i class="bi bi-pencil"></i>
                    </button>
                </div>
            </div>

            <!-- Switch to another organization -->
            <div class="pm-section" v-if="otherOrganizations.length > 0">
                <div class="pm-section__label">Cambiar de organización</div>

                <div class="pm-orgList">
                    <button v-for="organization in otherOrganizations"
                        class="pm-org pm-org--switch"
                        :key="organization.PKOrganization"
                        @click="ChangeOrganization(organization.PKOrganization)">

                        <span class="pm-org__avatar">
                            <img v-if="organization.ImageURL" :src="organization.ImageURL" alt="">
                            <img v-else src="../../images/OrganizationIcon.png" alt="">
                        </span>

                        <span class="pm-org__name">{{ organization.Organization }}</span>

                        <i class="bi bi-chevron-right pm-org__chevron"></i>
                    </button>
                </div>
            </div>

            <!-- Sign out -->
            <button class="pm-signout" @click="logout">
                <i class="bi bi-box-arrow-left"></i>
                <span>Cerrar sesión</span>
            </button>
        </div>
    </div>
</template>

<script>
import { useStore } from '@/store/DefaultStore.js';
import { useSessionStore } from '@/store/SessionStore';
import { useRouter } from 'vue-router';
import ShowNotification from '@/services/ToastService.js';

export default {
    name: 'ProfileMenu',

    data() {
        return {
            PKUser: false,
            MenuIsVisible: false
        }
    },

    computed: {
        // Single pass over store.Organizations: splits it into the current
        // org and every other org, instead of a separate find()/filter().
        organizationSplit() {
            const otherOrganizations = [];
            let currentOrganization = null;

            for (const organization of this.store.Organizations) {
                if (organization.PKOrganization == this.store.CurrentOrganizationPK) {
                    currentOrganization = organization;
                }
                else {
                    otherOrganizations.push(organization);
                }
            }

            return { currentOrganization, otherOrganizations };
        },

        currentOrganization() {
            return this.organizationSplit.currentOrganization;
        },

        currentOrganizationImage() {
            return this.currentOrganization?.ImageURL;
        },

        // Every org except the one the user is currently inside.
        otherOrganizations() {
            return this.organizationSplit.otherOrganizations;
        }
    },

    setup() {
        const store = useStore();
        const router = useRouter();
        const sessionStore = useSessionStore();

        return {
            store, router, sessionStore
        }
    },

    methods: {
        UpdateOrganization()
        {
          if(this.$route.name != 'organizations'){
            this.$parent.UpdateOrganization();
          }
          else {
            this.emitter.emit("OpenOrganizationModalFromNavbar");
          }
        },

        GoToConfig()
        {
          this.router.push({ name: 'settings' });
          this.$refs.userButtonRef.click();
        },

        ChangeOrganization(PKOrganization) {
            if(PKOrganization == this.store.CurrentOrganizationPK)
              return;

            this.store.SetLoader(true);
            this.sessionStore.SwitchOrganization(PKOrganization);
            this.router.go(0);
        },

        LoadProfileModal() {
            this.$parent.OpenProfileModal();
        },

        async logout() {
            await this.sessionStore.LogOut();
            await this.router.push({ name: 'login' });
            ShowNotification.Success('Se ha cerrado la sesión correctamente');
        },
    }
}
</script>

<style scoped>
/* ---------- Local tokens (mirror the redesigned navbar / notifications world) ---------- */
.profileDropdown {
    /* Raisin panel, lavender as the single signature accent. Values come from
       the shared dropdown-panel tokens in App.vue so this panel can't drift
       out of sync with the notifications panel it mirrors. */
    --pm-accent: var(--LativoLightPurple);
    --pm-ink: var(--DropdownPanelInk);
    --pm-ink-dim: var(--DropdownPanelInkDim);
    --pm-ink-faint: var(--DropdownPanelInkFaint);
    --pm-hover: var(--DropdownPanelHover);
    --pm-surface: var(--DropdownPanelSurface);
    --pm-line: var(--DropdownPanelLine);
    --pm-danger: #ff6b6d;
    --pm-danger-wash: rgba(255, 77, 79, 0.12);
    --pm-danger-line: rgba(255, 77, 79, 0.28);
    --pm-ease: var(--DropdownPanelEase);

    max-width: 500px;
}

/* ---------- Trigger ---------- */
.userButton {
    width: auto;
}

.profileTrigger {
    gap: 6px;
}

/* Two shapes so the two images read as distinct at a glance:
   organization = rounded square, user photo = circle. */
.profileTrigger .orgAvatar {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    object-fit: cover;
}

.profileTrigger .userAvatar {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    object-fit: cover;
}

.profileTrigger .userAvatar--initials {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--LativoLightPurple);
    color: var(--LativoDarkPurple);
    font-size: 11px;
    font-weight: bold;
    line-height: 1;
    user-select: none;
}

/* ---------- Panel ---------- */
/* Positioning, entrance animation, background/border/shadow, and z-index come
   from the shared .staticDropdownPanel utility (App.vue) — only this panel's
   own size/spacing stays local. */
.profileMenu {
    margin-top: 8px;
    width: 340px;
    max-width: calc(100vw - 16px);
    padding: 8px;
    border-color: var(--pm-line);
    color: var(--pm-ink);
    text-align: left;
}

/* ---------- Shared icon button (navbar / notifications language) ---------- */
.pm-iconBtn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background-color: transparent;
    border: none;
    border-radius: 9px;
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease, transform 140ms var(--pm-ease);
    -webkit-tap-highlight-color: transparent;
}

.pm-iconBtn i {
    font-size: 15px;
    color: inherit;
    margin: 0;
}

.pm-iconBtn:active {
    transform: scale(0.92);
}

.pm-iconBtn:focus-visible {
    outline: 2px solid var(--pm-accent);
    outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
    .pm-iconBtn:hover {
        background-color: var(--pm-hover);
        color: #ffffff;
    }
}

/* ---------- Identity ---------- */
.pm-identity {
    display: flex;
    align-items: center;
    gap: 13px;
    padding: 8px 6px 14px;
}

.pm-avatar {
    position: relative;
    flex-shrink: 0;
    width: 60px;
    height: 60px;
    padding: 0;
    border: none;
    border-radius: 50%;
    overflow: hidden;
    cursor: pointer;
    background-color: var(--LativoLightPurple);
    -webkit-tap-highlight-color: transparent;
}

.pm-avatar__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: filter 200ms ease;
}

.pm-avatar__initials {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: var(--LativoDarkPurple);
    font-family: 'SwitzerBold', sans-serif;
    font-size: 22px;
    line-height: 1;
    user-select: none;
    transition: filter 200ms ease;
}

/* Pencil overlay reveals on hover of the photo — the edit affordance. */
.pm-avatar__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    background-color: rgba(31, 31, 44, 0.55);
    opacity: 0;
    transition: opacity 200ms ease;
    pointer-events: none;
}

.pm-avatar__overlay i {
    font-size: 17px;
}

.pm-avatar:focus-visible {
    outline: 2px solid var(--pm-accent);
    outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
    .pm-avatar:hover .pm-avatar__overlay {
        opacity: 1;
    }

    .pm-avatar:hover .pm-avatar__img,
    .pm-avatar:hover .pm-avatar__initials {
        filter: brightness(0.72);
    }
}

.pm-identity__text {
    flex: 1;
    min-width: 0;
}

.pm-name {
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 15px;
    line-height: 1.25;
    color: var(--pm-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pm-email {
    margin-top: 2px;
    font-size: 12.5px;
    line-height: 1.3;
    color: var(--pm-ink-dim);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pm-identity__settings {
    align-self: flex-start;
    margin-top: -2px;
}

/* ---------- Sections ---------- */
.pm-section {
    padding: 12px 6px 4px;
    border-top: 1px solid var(--pm-line);
}

.pm-section__label {
    margin: 0 4px 8px;
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 11px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--pm-ink-dim);
}

/* ---------- Organization rows ---------- */
.pm-org {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 9px 10px;
    background-color: transparent;
    border: none;
    border-radius: 11px;
    color: var(--pm-ink);
    text-align: left;
    -webkit-tap-highlight-color: transparent;
}

.pm-org__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border-radius: 50%;
    overflow: hidden;
    background-color: var(--pm-surface);
    border: 1px solid var(--pm-line);
}

.pm-org__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.pm-org__name {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Current org reads as a settled fact — its lavender-tinted avatar ring marks it. */
.pm-org--current {
    background-color: var(--pm-surface);
    cursor: default;
}

.pm-org--current .pm-org__avatar {
    border-color: rgba(222, 228, 255, 0.28);
}

.pm-org__edit {
    width: 30px;
    height: 30px;
    margin-left: auto;
}

.pm-org__edit i {
    font-size: 13px;
}

/* Switchable orgs: full-row buttons with a hover wash + a chevron that drifts. */
.pm-org--switch {
    cursor: pointer;
    transition: background-color 160ms ease, transform 140ms var(--pm-ease);
}

.pm-org__chevron {
    flex-shrink: 0;
    font-size: 12px;
    color: var(--pm-ink-faint);
    transition: transform 200ms var(--pm-ease), color 160ms ease;
}

.pm-org--switch:active {
    transform: scale(0.99);
    background-color: var(--pm-hover);
}

.pm-org--switch:focus-visible {
    outline: 2px solid var(--pm-accent);
    outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
    .pm-org--switch:hover {
        background-color: var(--pm-hover);
    }

    .pm-org--switch:hover .pm-org__chevron {
        transform: translateX(3px);
        color: var(--pm-accent);
    }
}

.pm-orgList {
    max-height: 40vh;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(222, 228, 255, 0.2) transparent;
}

.pm-orgList::-webkit-scrollbar {
    width: 8px;
}

.pm-orgList::-webkit-scrollbar-thumb {
    background-color: rgba(222, 228, 255, 0.18);
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
}

/* ---------- Sign out ---------- */
.pm-signout {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    height: 44px;
    margin-top: 12px;
    padding: 0 14px;
    background-color: var(--pm-surface);
    border: 1px solid var(--pm-line);
    border-radius: 11px;
    color: var(--pm-ink);
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease, border-color 160ms ease, transform 140ms var(--pm-ease);
    -webkit-tap-highlight-color: transparent;
}

.pm-signout i {
    font-size: 16px;
    transition: transform 200ms var(--pm-ease);
}

.pm-signout:active {
    transform: scale(0.98);
}

.pm-signout:focus-visible {
    outline: 2px solid var(--pm-danger);
    outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
    .pm-signout:hover {
        background-color: var(--pm-danger-wash);
        border-color: var(--pm-danger-line);
        color: var(--pm-danger);
    }

    .pm-signout:hover i {
        transform: translateX(-3px);
    }
}

/* ---------- Mobile: full-width sheet, pinned under the navbar ---------- */
@media screen and (max-width: 915px) {
    /* Fixed to the viewport (not anchored to the tiny trigger) so it spans the
       full width exactly — no right-side spill, no horizontal scroll. Mirrors
       the notifications sheet. */
    .profileMenu {
        position: fixed !important;
        top: 60px !important;
        left: 0 !important;
        right: 0 !important;
        margin-top: 0;
        width: 100%;
        max-width: 100%;
        max-height: calc(100dvh - 60px);
        overflow-y: auto;
        padding: 10px 12px 14px;
        border-radius: 0 0 16px 16px;
        border-left: none;
        border-right: none;
        border-bottom: 1px solid var(--pm-line);
    }

    /* Sheet drops straight down from the bar, not out of a corner. */
    .profileMenu.show {
        transform-origin: top center;
    }

    .pm-orgList {
        max-height: none;
    }
}

/* ---------- Reduced motion ---------- */
@media (prefers-reduced-motion: reduce) {
    .pm-iconBtn:active,
    .pm-org--switch:active,
    .pm-signout:active {
        transform: none;
    }

    .pm-org--switch:hover .pm-org__chevron,
    .pm-signout:hover i {
        transform: none;
    }

    .pm-avatar__img,
    .pm-avatar__initials,
    .pm-avatar__overlay {
        transition: opacity 160ms ease;
    }
}
</style>
